// Site footer. Three clusters in one row — logo (left), nav
// (centered, with dot separators), copyright (right). Quieter
// border, fixed-height row, pill-style hover so the links feel
// considered without shouting. RootLayout pins it to the bottom
// on short pages via mt-auto + min-h-screen.

import { Fragment } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faCircleQuestion,
  faLightbulb,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";

import { useT } from "../../i18n/I18nContext";
// Brand wordmark — same transparent-background PNG the navbar
// uses, rendered bigger here so the row carries some weight
// against the body content above.
import logo from "../../assets/plumescript-logo-removebg.png";

// Each link carries an icon for the very-tiny-screen branch
// where the text label collapses ("min-[400px]:inline" hides
// the label below 400px to keep the row single-line on every
// phone). Icons stay visible at every width so the link's
// affordance is consistent across breakpoints.
const NAV_LINKS = [
  { to: "/help", key: "nav.help", icon: faCircleQuestion },
  { to: "/contact", key: "nav.contact", icon: faPhone },
  { to: "/curiosities", key: "nav.curiosities", icon: faLightbulb },
  { to: "/terms", key: "footer.terms", icon: faCircleInfo },
];

export default function Footer() {
  const t = useT();
  const year = new Date().getFullYear();

  return (
    <footer
      role="contentinfo"
      aria-label={t("footer.label")}
      className="mt-auto border-t border-border/70 bg-surface text-secondText"
    >
      {/* Single row on every viewport. The nav switches to
          icon-only below 400px (``min-[400px]:inline`` reveals
          the labels at and above 400px) so the row always fits
          horizontally — even FR's "Aide · Nous écrire · CGU"
          doesn't blow past the logo + copyright on a 320px
          phone. Above 400px the labels return alongside the
          icons and the row reads as the desktop footer. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 min-h-[3.5rem] flex items-center gap-x-3 sm:gap-x-6">
        {/* Logo doubles as Home — same affordance as the navbar.
            ``app-logo`` class wires the per-theme filter chain
            already shared with the navbar (see index.css):
            light keeps the brand cyan, dark inverts the wordmark
            to white, playful hue-rotates toward pink. Object
            position pins the visible glyphs to the left edge of
            the bounding box (the PNG has whitespace baked in)
            so the brand sits cleanly against the row's leading
            padding instead of floating mid-cell. */}
        <Link
          to="/"
          aria-label="Plumescript"
          className="shrink-0 inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md -ml-1"
        >
          <img
            src={logo}
            alt=""
            aria-hidden="true"
            className="app-logo h-8 w-auto object-contain object-left"
          />
        </Link>

        {/* Centered nav. Middle-dot separators are decoration, not
            list-item structure — kept out of the AT tree with
            aria-hidden + ``select-none`` so a triple-click
            highlights the link text only. The links themselves get
            a quiet pill-hover instead of a colour swap, which felt
            shouty across the navbar/footer pair. */}
        {/* Right-aligned nav, balancing the logo cluster on the
            left. Below 400px the labels collapse to icon-only —
            each Link still carries the translated label as
            ``aria-label`` + ``title`` so screen readers and
            pointer-hover tooltips both work. The middle-dot
            separators only render alongside the labels (above
            400px) — between bare icons they read as visual
            noise. */}
        <nav
          aria-label={t("footer.label")}
          className="flex items-center gap-x-0.5 text-[13px] flex-1 min-w-0 justify-end"
        >
          {NAV_LINKS.map(({ to, key, icon }, i) => {
            const label = t(key);
            return (
              <Fragment key={to}>
                {i > 0 ? (
                  <span
                    aria-hidden="true"
                    className="hidden min-[400px]:inline text-secondText/30 px-1 select-none"
                  >
                    ·
                  </span>
                ) : null}
                <Link
                  to={to}
                  aria-label={label}
                  title={label}
                  className="inline-flex items-center px-2 py-1 rounded-md whitespace-nowrap hover:text-text hover:bg-surfaceAlt focus:outline-none focus-visible:text-text focus-visible:bg-surfaceAlt transition"
                >
                  {/* Icon-only on the very-tiny branch (< 400px),
                      text-only at every other width. The two are
                      mutually exclusive so the link reads as one
                      affordance instead of icon-plus-redundant-
                      label noise on the wider rows. */}
                  <FontAwesomeIcon
                    icon={icon}
                    aria-hidden="true"
                    className="text-[12px] opacity-80 min-[400px]:hidden"
                  />
                  <span className="hidden min-[400px]:inline">{label}</span>
                </Link>
              </Fragment>
            );
          })}
        </nav>

        <p className="text-[11px] tabular-nums text-secondText/70 shrink-0 hidden sm:block">
          © {year} Plumescript
        </p>
      </div>
    </footer>
  );
}
